import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  AppInput,
  AppModal,
  AppMultiSelect,
  AppRemixIcon,
  AppSingleSelect,
} from '@/layout/AppComponents';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { Formik } from 'formik';
import ReactDOMServer from 'react-dom/server';
import * as currentPageAction from '../../../../../store/actions/currentPageAction';
import Translator from '../../../../components/helpers/Translator';
import { axiosPath, path } from '../../../../../routing/helpers/router';
import { getInecoRiskManagerFilterInitialValues } from '../../../../../minions/forms';
import AuxSidebar from '../../../../Sidebar/AuxSidebar';
import useIsMounted from '../../../../components/hooks/useIsMounted';
import _ from 'lodash';
import { getPeriodOptions } from '../tools/riskManagerTools';

export default function FilterSidebarBlock() {
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const navigate = useNavigate();
  const { moduleBridgeHex, filtersHex } = useParams();
  const auxSidebar = useSelector((state) => state.currentPage?.auxSidebar?.opened);
  const moduleData = useSelector(
    (state) => state?.module?.[moduleBridgeHex]?.moduleData,
  );
  const filters = useSelector((state) => state.currentPage?.filters);
  const [openOnce, setOpenOnce] = useState(true);
  const [severityLevelModal, setSeverityLevelModal] = useState(false);
  const handleAuxSidebar = () => {
    if (!openOnce) setOpenOnce(true);
    if (auxSidebar !== 'filters') {
      dispatch(currentPageAction.change('auxSidebar', { opened: 'filters' }));
    } else {
      dispatch(currentPageAction.change('auxSidebar', { opened: null }));
    }
  };

  const goToEvaluation = (hexCode) =>
    navigate(
      path({
        name: 'user_module_inecoRiskManager_risk_list',
        params: {
          moduleBridgeHex,
          page: 1,
          filtersHex: hexCode,
        },
      }),
    );

  // > GENERATE FILTER HEX CODE
  const generateFilterHexCode = async (values, setSubmitting) =>
    axios
      .post(axiosPath('api_r_user_module_inecoRiskManager_generateFilterHexCode'), {
        moduleBridgeHex,
        filters: values,
      })
      .then(({ data }) => {
        setSubmitting(false);
        goToEvaluation(data.hexCode);
        dispatch(currentPageAction.change('auxSidebar', { opened: null }));
      });

  const handleOnSubmit = (values, { setSubmitting }) => {
    generateFilterHexCode(values, setSubmitting);
  };
  // < GENERATE FILTER HEX CODE

  // > FILTERS
  const getFilters = useCallback(
    async () =>
      axios
        .post(axiosPath('api_r_user_module_inecoRiskManager_getFilters'), {
          moduleBridgeHex,
          filtersHex,
        })
        .then(({ data }) => {
          if (isMounted.current) {
            dispatch(currentPageAction.change('filters', data));
          }
        }),
    [moduleBridgeHex, filtersHex],
  );

  useEffect(() => {
    if (filtersHex !== '0') {
      getFilters();
    } else {
      dispatch(
        currentPageAction.change(
          'filters',
          getInecoRiskManagerFilterInitialValues(),
          true,
        ),
      );
    }
  }, [filtersHex]);
  // < FILTERS

  const onClickSeverityLevel = useCallback(
    (probability, impact, setFieldValue) => {
      setFieldValue('probabilities', [probability]);
      setFieldValue('impacts', [impact + 1]);
      setSeverityLevelModal(false);
    },
    [moduleData],
  );

  return (
    <>
      <li
        className={auxSidebar && auxSidebar === 'filters' ? 'active' : ''}
        onClick={() => handleAuxSidebar()}
      >
        <span className="sidebar-element">
          <AppRemixIcon icon="equalizer" className="sidebar-icon" />{' '}
          <span className="sb-title">
            <Translator file="module-legalCompliance" name="filtering" />
          </span>
        </span>
      </li>
      <AuxSidebar
        title={<Translator file="module-legalCompliance" name="filtering" />}
        isOpen={auxSidebar && auxSidebar === 'filters'}
        width="350px"
        position="right"
        onClose={handleAuxSidebar}
      >
        {openOnce && (
          <>
            {filters && (
              <Formik
                validateOnChange={false}
                validateOnBlur={false}
                enableReinitialize
                initialValues={filters}
                onSubmit={handleOnSubmit}
              >
                {({
                  setFieldValue,
                  handleSubmit,
                  values,
                  errors,
                  isSubmitting,
                  resetForm,
                }) => (
                  <>
                    <div className="d-flex mt-3 mb-3">
                      <span
                        className="btn btn-info btn-block"
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                      >
                        <Translator file="module-legalCompliance" name="filtering" />
                      </span>
                      {filtersHex !== '0' && (
                        <span
                          title={ReactDOMServer.renderToString(
                            <Translator
                              file="module-legalCompliance"
                              name="delete.filters"
                            />,
                          )}
                          className="btn btn-outline-danger ml-1"
                          onClick={() => goToEvaluation(0)}
                        >
                          <AppRemixIcon icon="delete-bin-6" />
                        </span>
                      )}
                    </div>
                    {/* {permissions?.fields?.category && (
                      <AppMultiSelect
                        options={moduleData?.inputs?.categories}
                        title="Categoría"
                        value={values?.categories}
                        onChange={(opt) => {
                          setFieldValue('categories', opt);
                        }}
                        allowSelectAll
                        hasSearchBox
                        hasSelectOptions
                      />
                    )} */}
                    <AppMultiSelect
                      options={moduleData?.inputs?.types}
                      title="Tipo"
                      value={values?.types}
                      onChange={(opt) => {
                        setFieldValue('types', opt);
                      }}
                      allowSelectAll
                      hasSearchBox
                      hasSelectOptions
                    />
                    <AppInput
                      title="Propietarios"
                      onChange={(value) => setFieldValue('additionalOwners', value)}
                      value={values.additionalOwners}
                      error={errors.additionalOwners}
                      isClearable
                    />
                    <AppMultiSelect
                      options={getPeriodOptions()}
                      title="Plazos"
                      value={values?.periods}
                      error={errors?.periods}
                      onChange={(opt) => setFieldValue('periods', opt)}
                      isClearable
                    />
                    {/* <AppMultiSelect
                      options={moduleData?.inputs?.organizationProcesses}
                      title="Procesos de la organización"
                      value={values?.organizationProcesses}
                      onChange={(opt) => {
                        setFieldValue('organizationProcesses', opt);
                      }}
                      allowSelectAll
                      hasSearchBox
                      hasSelectOptions
                    /> */}
                    <AppMultiSelect
                      options={moduleData?.inputs?.strategicGoals}
                      title="Objetivo estratégico"
                      value={values?.strategicGoals}
                      onChange={(opt) => {
                        setFieldValue('strategicGoals', opt);
                      }}
                      allowSelectAll
                      hasSearchBox
                      hasSelectOptions
                    />
                    <AppMultiSelect
                      options={moduleData?.inputs?.states.filter((s) =>
                        [7, 8].includes(s?.value),
                      )}
                      title="Estado"
                      value={values?.states}
                      onChange={(opt) => {
                        setFieldValue('states', opt);
                      }}
                      allowSelectAll
                      hasSearchBox
                      hasSelectOptions
                    />
                    <AppMultiSelect
                      options={moduleData?.inputs?.probabilities}
                      title="Probabilidad"
                      value={values?.probabilities}
                      onChange={(opt) => {
                        setFieldValue('probabilities', opt);
                      }}
                      allowSelectAll
                      hasSearchBox
                      hasSelectOptions
                    />
                    <AppMultiSelect
                      options={moduleData?.inputs?.impacts}
                      title="Impacto"
                      value={values?.impacts}
                      onChange={(opt) => {
                        setFieldValue('impacts', opt);
                      }}
                      allowSelectAll
                      hasSearchBox
                      hasSelectOptions
                    />
                    <AppMultiSelect
                      options={moduleData?.inputs?.severities}
                      title="Severidad"
                      value={values?.severities}
                      onChange={(opt) => {
                        setFieldValue('severities', opt);
                      }}
                      allowSelectAll
                      hasSearchBox
                      hasSelectOptions
                    />
                    <div className="text-center">
                      <button
                        type="button"
                        className="btn btn btn-sm btn-success"
                        onClick={() => setSeverityLevelModal(true)}
                      >
                        Nivel de Severidad
                      </button>
                    </div>

                    <AppModal
                      show={severityLevelModal}
                      title="Nivel de Severidad"
                      onClose={() => setSeverityLevelModal(false)}
                      size="lg"
                    >
                      <div className="table-responsive severity-table">
                        <table className="table">
                          <thead>
                            <tr>
                              <th colSpan="7" className="text-center">
                                Nivel de Severidad
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td rowSpan="7">
                                <b>Probabilidad</b>
                              </td>
                              <td className="text-center">Frecuente</td>
                              {!_.isEmpty(moduleData) &&
                                moduleData?.inputs?.severityFilterTable[0].map(
                                  (c, i) => (
                                    <td
                                      style={{
                                        backgroundColor:
                                          i === 0
                                            ? '#00b050'
                                            : i === 1
                                              ? '#ffc000'
                                              : '#f00',
                                      }}
                                      className="text-center pointer"
                                      onClick={() =>
                                        onClickSeverityLevel(5, i, setFieldValue)
                                      }
                                    >
                                      {c}
                                    </td>
                                  ),
                                )}
                            </tr>
                            <tr>
                              <td className="text-center">Razonadamente probable</td>
                              {!_.isEmpty(moduleData) &&
                                moduleData?.inputs?.severityFilterTable[1].map(
                                  (c, i) => (
                                    <td
                                      style={{
                                        backgroundColor:
                                          i <= 1
                                            ? '#00b050'
                                            : i <= 3
                                              ? '#ffc000'
                                              : '#f00',
                                      }}
                                      className="text-center pointer"
                                      onClick={() =>
                                        onClickSeverityLevel(4, i, setFieldValue)
                                      }
                                    >
                                      {c}
                                    </td>
                                  ),
                                )}
                            </tr>
                            <tr>
                              <td className="text-center">Remoto</td>
                              {!_.isEmpty(moduleData) &&
                                moduleData?.inputs?.severityFilterTable[2].map(
                                  (c, i) => (
                                    <td
                                      style={{
                                        backgroundColor:
                                          i <= 2
                                            ? '#00b050'
                                            : i <= 4
                                              ? '#ffc000'
                                              : '#f00',
                                      }}
                                      className="text-center pointer"
                                      onClick={() =>
                                        onClickSeverityLevel(3, i, setFieldValue)
                                      }
                                    >
                                      {c}
                                    </td>
                                  ),
                                )}
                            </tr>
                            <tr>
                              <td className="text-center">Extremadamente remoto</td>
                              {!_.isEmpty(moduleData) &&
                                moduleData?.inputs?.severityFilterTable[3].map(
                                  (c, i) => (
                                    <td
                                      style={{
                                        backgroundColor:
                                          i <= 3
                                            ? '#00b050'
                                            : i <= 4
                                              ? '#ffc000'
                                              : '#f00',
                                      }}
                                      className="text-center pointer"
                                      onClick={() =>
                                        onClickSeverityLevel(2, i, setFieldValue)
                                      }
                                    >
                                      {c}
                                    </td>
                                  ),
                                )}
                            </tr>
                            <tr>
                              <td className="text-center">
                                Extremadamente improbable
                              </td>
                              {!_.isEmpty(moduleData) &&
                                moduleData?.inputs?.severityFilterTable[4].map(
                                  (c, i) => (
                                    <td
                                      style={{ backgroundColor: '#00b050' }}
                                      className="text-center pointer"
                                      onClick={() =>
                                        onClickSeverityLevel(1, i, setFieldValue)
                                      }
                                    >
                                      {c}
                                    </td>
                                  ),
                                )}
                            </tr>
                            <tr>
                              <td />
                              <td className="text-center">Muy bajo</td>
                              <td className="text-center">Bajo</td>
                              <td className="text-center">Medio</td>
                              <td className="text-center">Alto</td>
                              <td className="text-center">Muy alto</td>
                            </tr>
                            <tr>
                              <td colSpan="7" className="text-center">
                                <b>Impacto</b>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </AppModal>
                  </>
                )}
              </Formik>
            )}
          </>
        )}
      </AuxSidebar>
    </>
  );
}
